import {httpBoundary} from '@werkingflow/trust-boundary';
import {z} from 'zod';
import {useEffect,useState} from 'react';
import type { BuehneAnsicht } from './GuideHof';

/** Bestehender Arbeitsplan, lesbar am Auftrag statt als neues Formular. */
export function GuideAuftrag({ buehne }: { buehne: Pick<BuehneAnsicht, 'arbeitsplan' | 'auftrag'> }) {
  const p = buehne.arbeitsplan;
  if (!p) return null;
  const zeilen = [
    ['Auftrag', p.ziel],
    ['Fertig, wenn', p.fertigWenn],
    ['Zielabdeckung', p.beratung?.zielabdeckung],
    ['Aktueller Stand', p.meilenstein],
    ['Nächster Schritt', p.naechsterSchritt],
    ['Zuständig', /^session:[0-9a-f-]+$/i.test(p.zustaendig) ? 'Beauftragte Sitzung' : p.zustaendig],
  ];
  return <details data-ai-id="guide-auftrag-details" style={{ marginBottom: 10, fontSize: 12 }}>
    <summary style={{ cursor: 'pointer', color: 'var(--tn-text-muted)' }}>Auftrag und Arbeitsstand im Detail</summary>
    <dl data-ai-id="guide-auftrag" style={{ margin: '0 0 10px', fontSize: 12, lineHeight: 1.5 }}>
    {zeilen.filter(([, text]) => !!text).map(([titel, text]) => <div key={titel} style={{ marginBottom: 4 }}>
      <dt style={{ display: 'inline', fontWeight: 600 }}>{titel}: </dt>
      <dd style={{ display: 'inline', margin: 0 }}>{text}</dd>
    </div>)}
  </dl></details>;
}


// Persönliche Beratung ist Hauptinhalt; technische Nachweise bleiben in den Details.
export function GuideBeratung({ buehne }: { buehne: Pick<BuehneAnsicht, 'beratung_bereit' | 'arbeitsplan' | 'hauptstatus' | 'vorlagepruefung'> }) {
  const beratung = buehne.arbeitsplan?.beratung;
  if (!beratung) return null;
  return <section aria-label="Beratung" data-ai-id="guide-beratung" style={{ fontSize: 13, lineHeight: 1.55 }}>
    {!buehne.beratung_bereit && <small>Vorhandene Beratung · aktuelle Fassung noch nicht vollständig geprüft</small>}
    <p style={{ whiteSpace: 'pre-wrap' }}>{beratung.einordnung}</p>
    <p style={{ whiteSpace: 'pre-wrap' }}>{beratung.empfehlung}</p>
  </section>;
}

const BeratungsfassungAntwort=z.object({id:z.string(),inhalt:z.object({html:z.string().nullable().optional(),titel:z.string().optional()})});

/** Prior answer versions remain accessible without executing historical embedded scripts. */
export function GuideBeratungsstand({buehne}:{buehne:BuehneAnsicht}) {
  const [fassung,setFassung]=useState('');
  const [inhalt,setInhalt]=useState<{html?:string|null; titel?:string}|null>(null);
  const [fehler,setFehler]=useState('');
  useEffect(()=>{setFassung('');setInhalt(null);setFehler('');},[buehne.id]);
  useEffect(()=>{
    if(!fassung) {setInhalt(null);return;}
    let aktuell=true;setFehler('');setInhalt(null);
    httpBoundary({name:'cui.beratung.historie',url:`/api/guide/buehnen/${encodeURIComponent(buehne.id)}/beratungsfassungen/${fassung}`,
      schema:BeratungsfassungAntwort,timeoutMs:15000})
      .then(d=>{if(d.id!==fassung)throw new Error('Die historische Fassung stimmt nicht überein.');if(aktuell)setInhalt(d.inhalt);})
      .catch(e=>{if(aktuell)setFehler(e instanceof Error ? e.message : 'Historische Beratung konnte nicht geladen werden.');});
    return ()=>{aktuell=false;};
  },[buehne.id,fassung]);
  const p=buehne.vorlagepruefung;
  const ruht = buehne.hauptstatus === 'abgeschlossen' || buehne.hauptstatus === 'von_dir_zurueckgestellt' || buehne.raum === 'pausenraum';
  const taetigkeit = buehne.arbeitsmessung === 'unbekannt' || buehne.berater_messfehler
    ? 'Aktuelle Tätigkeit nicht bestätigt.'
    : buehne.arbeitsmessung === 'arbeitet' || buehne.arbeit_laeuft === true
      ? 'Laufende Arbeit gemessen.'
      : buehne.saal_bereich === 'zuarbeit'
        ? 'Keine eigene laufende Arbeit; Zuarbeit belegt.'
        : buehne.arbeitsmessung === 'nicht_arbeitend' || buehne.arbeit_laeuft === false
          ? 'Derzeit keine laufende Arbeit nachgewiesen.'
          : 'Aktuelle Tätigkeit nicht bestätigt.';
  const abhaengigkeiten = buehne.arbeitsplan?.abhaengigkeiten?.filter(a => !a.erledigt) ?? [];
  return <section aria-label="Beratungsstand" style={{marginBottom:12,fontSize:12,lineHeight:1.5}}>
    <strong>{buehne.hauptstatus_text ?? 'In Arbeit'}</strong>
    {!ruht && <div data-ai-id="guide-taetigkeit-kurz">{taetigkeit}</div>}
    {!ruht && abhaengigkeiten.length > 0 && <div>{abhaengigkeiten.length === 1 ? 'Eine offene Abhängigkeit' : `${abhaengigkeiten.length} offene Abhängigkeiten`} im Arbeitsplan.</div>}
    {!buehne.beratung_bereit && !ruht && <div>Die Prüfung der aktuellen Beratung ist noch offen.</div>}
    {(buehne.arbeitsinformation || p || abhaengigkeiten.length > 0) && <details data-ai-id="guide-pruefung-details" style={{overflowWrap:'anywhere'}}>
      <summary>Arbeitsstand und technische Prüfdetails</summary>
      {abhaengigkeiten.length > 0 && <><strong>Offene Abhängigkeiten</strong><ul>{abhaengigkeiten.map((a, i) => <li key={i}>{a.wartetAuf}{a.beleg && <div>{a.beleg}</div>}</li>)}</ul></>}
      {buehne.arbeitsinformation && <p style={{whiteSpace:'pre-wrap'}}>{buehne.arbeitsinformation}</p>}
      {p && <><strong>Prüfumfang und Befund</strong><p style={{whiteSpace:'pre-wrap'}}>{p.befund}</p><small>{p.am} · {p.status}{p.beratungsMassstab!==2 ? ' · früherer Prüfmaßstab' : ''}</small></>}
    </details>}
    {!!buehne.beratungsfassungen?.length && <details><summary>Frühere Beratungsfassungen</summary>
      <label>Unveränderte Grundlage einer früheren Antwort <select value={fassung} onChange={e=>setFassung(e.target.value)} style={{maxWidth:'100%'}}>
        <option value="">Fassung auswählen</option>
        {[...buehne.beratungsfassungen].reverse().map(f=><option key={f.id} value={f.id}>{f.am} · {f.anlass}</option>)}
      </select></label>
      {fehler && <p role="alert">{fehler}</p>}
      {inhalt?.html && <iframe title={`Historische Beratung: ${inhalt.titel ?? ''}`} sandbox="" srcDoc={inhalt.html} style={{width:'100%',height:420,border:'1px solid var(--tn-border)'}}/>}
    </details>}
  </section>;
}
